import React, { useContext, useEffect } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import PublishersStep from '@App/kit/scenes/WorkScenes/Steps/PublishersSteps/PublishersStep'
import { StoresContext } from '@App/contexts'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import RootStore from '@App/stores/root-store'
import { useLocation } from 'react-router'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import ClaimSearch from '@Scenes/RightsholderScene/MatchClaim/Claim/ClaimSearch'
import { ICONS } from '@Const/common'
import WizardFlow from '@Components/WizardFlow'
import ClaimWizardNavigationBar from '@Scenes/RightsholderScene/MatchClaim/ClaimWizardFlow/ClaimWizardNavigationBar'
import WizardFlowStep from '@Components/WizardFlow/wizard-flow-step'
import ReviewWorkFormScene from '@Scenes/WorkScenes/ReviewWorkFormScene'
import ClaimSubmissionStep from '@Scenes/WorkScenes/Steps/ClaimSubmissionStep'

const ClaimWizardFlow = () => {
    const { wizardFlowStore, workStore, claimToolStore, preloaderStore } = useContext<RootStore>(StoresContext)
    const location = useLocation()

    const selectWork = async (id: number | null | undefined) => {
        if (!workStore.workById || workStore.workById.id !== id) {
            await workStore.getWorkById(id as number, FetchType.HFA)
        }
        const workDetails = workStore.workById
        if (workDetails) {
            workDetails.alternativeTitles = []
            workDetails.recordings = []
            workStore.setWorkDraftToLocal(workDetails)
        }

        wizardFlowStore.advanceIndex()
    }

    // Passing in a propertyId via location state when redirecting to claiming tool allows you to skip step 1
    // and go right into the claiming of a specified work
    const checkForProvidedWorkOverride = async () => {
        if (location.state?.propertyId) {
            preloaderStore.openMainPreloader(true)
            await selectWork(location.state?.propertyId)
            preloaderStore.openMainPreloader(false)
        }
    }

    useEffect(() => {
        analyticsSendPage('claim')
        checkForProvidedWorkOverride()
        return () => claimToolStore.resetClaimingToolWizard()
    }, [])

    const flow: WizardFlowStep[] = [
        {
            stepIndicator: {
                icon: ICONS.search,
                label: 'Search',
            },
            forms: [<ClaimSearch key="claim-search-step" selectWork={selectWork} />],
            navigationBar: <ClaimWizardNavigationBar />,
        },
        {
            stepIndicator: {
                icon: ICONS.publishers,
                label: 'Add Publishers & Shares',
            },
            forms: [<PublishersStep workType="claim-wizard" key="claim-publishers-step" />],
            navigationBar: <ClaimWizardNavigationBar />,
            onNextValidation: () => claimToolStore.onNextValidatePublishers() && workStore.onNextValidateOverclaim(),
        },
        {
            stepIndicator: {
                icon: ICONS.workDetails,
                label: 'Review',
            },
            forms: [<ReviewWorkFormScene workType="claim-wizard" key="claim-edit-work-scene" />],
            navigationBar: <ClaimWizardNavigationBar />,
            onNextValidation: () => workStore.onNextValidateOverclaim(),
        },
        {
            stepIndicator: {
                icon: ICONS.upload,
                label: 'Submit',
                displayFinishedIconAction: 'on',
            },
            forms: [<ClaimSubmissionStep key="claim-submission-step" />],
            navigationBar: <ClaimWizardNavigationBar />,
        },
    ]

    wizardFlowStore.setFlow(flow)

    return <WizardFlow heading="Claiming Tool" />
}

export default observer(ClaimWizardFlow)
